/**
 * Chess move notation utilities
 * Converts moves to algebraic notation (e.g., "e4", "Nf3", "Qxd5+")
 */

import { Move, GameState, PieceType } from './types';
import { squareToAlgebraic } from './board';
import { isInCheck, generateLegalMoves } from './moves';
import { isCheckmate } from './endgame';

/**
 * Get the letter for a piece type (empty string for pawns)
 */
function getPieceLetter(pieceType: PieceType): string {
  switch (pieceType) {
    case PieceType.King: return 'K';
    case PieceType.Queen: return 'Q';
    case PieceType.Rook: return 'R';
    case PieceType.Bishop: return 'B';
    case PieceType.Knight: return 'N';
    case PieceType.Pawn: return '';
  }
}

/**
 * Find all pieces of the same type and color that could move to the target square
 * Used for disambiguation (e.g., Nbd7 vs Nfd7)
 */
function findAmbiguousMoves(gameState: GameState, move: Move): Move[] {
  const allMoves: Move[] = generateLegalMoves(gameState);
  
  // Find all moves with same piece type, different source, same destination
  return allMoves.filter(m => 
    m.piece.type === move.piece.type &&
    m.piece.color === move.piece.color &&
    m.from !== move.from &&
    m.to === move.to
  );
}

/**
 * Get disambiguation string (file, rank, or both) if needed
 */
function getDisambiguation(gameState: GameState, move: Move): string {
  const ambiguousMoves = findAmbiguousMoves(gameState, move);
  
  if (ambiguousMoves.length === 0) {
    return '';
  }
  
  const fromFile = move.from % 8;
  const fromRank = Math.floor(move.from / 8);
  
  // Check if file is unique
  const sameFile = ambiguousMoves.some(m => m.from % 8 === fromFile);
  if (!sameFile) {
    return String.fromCharCode(97 + fromFile); // a-h
  }
  
  // Check if rank is unique
  const sameRank = ambiguousMoves.some(m => Math.floor(m.from / 8) === fromRank);
  if (!sameRank) {
    return String(fromRank + 1); // 1-8
  }
  
  // Both file and rank needed
  return squareToAlgebraic(move.from);
}

/**
 * Convert a move to algebraic notation
 * Examples: e4, Nf3, Qxd5+, O-O, e8=Q#
 */
export function moveToAlgebraic(move: Move, gameState: GameState, resultingState: GameState): string {
  // Castling
  if (move.isCastling) {
    // Kingside if king moves 2 squares to the right
    return move.to > move.from ? 'O-O' : 'O-O-O';
  }
  
  let notation = '';
  
  // Piece letter (empty for pawns)
  const pieceLetter = getPieceLetter(move.piece.type);
  notation += pieceLetter;
  
  // For pawns capturing, include the source file
  if (move.piece.type === PieceType.Pawn && move.captured) {
    const fromFile = move.from % 8;
    notation += String.fromCharCode(97 + fromFile); // a-h
  }
  
  // Disambiguation for non-pawns
  if (move.piece.type !== PieceType.Pawn) {
    notation += getDisambiguation(gameState, move);
  }
  
  // Capture symbol
  if (move.captured) {
    notation += 'x';
  }
  
  // Destination square
  notation += squareToAlgebraic(move.to);
  
  // Promotion
  if (move.promotion) {
    notation += '=' + getPieceLetter(move.promotion);
  }
  
  // Check or checkmate
  const inCheck = isInCheck(resultingState, resultingState.activeColor);
  if (inCheck) {
    const isCheckmateMove = isCheckmate(resultingState);
    notation += isCheckmateMove ? '#' : '+';
  }
  
  return notation;
}

/**
 * A move with its algebraic notation
 */
export interface AnnotatedMove {
  move: Move;
  notation: string;
  fen?: string; // Optional: for move navigation in future
}
